// src/pages/Dashboard.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import './Dashboard.css';

const Dashboard = () => {
  const [userStats, setUserStats] = useState(null);
  const [activeTab, setActiveTab] = useState('cash');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { currentUser, authToken } = useAuth(); // Get authToken from context

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const API_BASE = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;
        const statsResponse = await axios.get(`${API_BASE}/api/user/stats`, {
          headers: {
            Authorization: `Bearer ${authToken}` // Add authorization header
          }
        });
        setUserStats(statsResponse.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Failed to load dashboard data');
        // Set default user stats to prevent null errors
        setUserStats({
          wallet_balance: 0,
          total_games: 0,
          total_winnings: 0,
          win_rate: 0
        });
      } finally {
        setLoading(false);
      }
    };

    if (currentUser && authToken) {
      fetchDashboardData();
    } else {
      // If no current user, set loading to false
      setLoading(false);
    }
  }, [currentUser, authToken]);

  // Rest of your component remains the same...
  // Game variants data
  const gameVariants = {
    cash: [
      { id: 1, name: 'Deals Rummy', minPlayers: 2, maxPlayers: 6, entryFee: 25, prize: 250 },
      { id: 2, name: 'Pool Rummy', minPlayers: 2, maxPlayers: 6, entryFee: 50, prize: 500 },
      { id: 3, name: 'Pool Rummy', minPlayers: 2, maxPlayers: 4, entryFee: 10, prize: 100 },
      { id: 4, name: 'Best of 3', minPlayers: 2, maxPlayers: 4, entryFee: 100, prize: 1000 }
    ],
    practice: [
      { id: 5, name: 'Deals Rummy', minPlayers: 2, maxPlayers: 6, entryFee: 0, prize: 0 },
      { id: 6, name: 'Pool Rummy', minPlayers: 2, maxPlayers: 6, entryFee: 0, prize: 0 },
      { id: 7, name: 'Pool Rummy', minPlayers: 2, maxPlayers: 4, entryFee: 0, prize: 0 }
    ],
    tournaments: [
      { id: 8, name: 'Daily Tournament', minPlayers: 10, maxPlayers: 100, entryFee: 100, prize: 5000 },
      { id: 9, name: 'Weekly Championship', minPlayers: 50, maxPlayers: 200, entryFee: 500, prize: 25000 },
      { id: 10, name: 'Monthly Mega', minPlayers: 100, maxPlayers: 500, entryFee: 1000, prize: 100000 }
    ]
  };

  const joinGame = async (gameId, entryFee) => {
    try {
      // Check if userStats is available
      if (!userStats) {
        setError('User data not loaded. Please try again.');
        return;
      }
      
      if (entryFee > 0 && userStats.wallet_balance < entryFee) {
        setError('Insufficient balance to join this game');
        return;
      }
      
      // Create or join a game
      const API_BASE = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;
      const response = await axios.post(`${API_BASE}/api/games`, {
        name: gameVariants[activeTab].find(g => g.id === gameId).name,
        type: activeTab,
        max_players: gameVariants[activeTab].find(g => g.id === gameId).maxPlayers,
        buy_in: entryFee
      }, {
        headers: {
          Authorization: `Bearer ${authToken}` // Add authorization header
        }
      });
      
      // Redirect to game page
      window.location.href = `/game/${response.data.game.id}`;
    } catch (error) {
      console.error('Error joining game:', error);
      setError('Failed to join game');
    }
  };

  if (loading) {
    return <div className="dashboard-loading">Loading dashboard...</div>;
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="user-welcome">
          <h2>Welcome back, {currentUser?.username}!</h2>
          <div className="wallet-balance">
            <span className="balance-label">Wallet Balance:</span>
            <span className="balance-amount">₹{userStats?.wallet_balance || 0}</span>
          </div>
        </div>
        
        {userStats && (
          <div className="user-stats">
            <div className="stat">
              <span className="stat-value">{userStats.total_games || 0}</span>
              <span className="stat-label">Games Played</span>
            </div>
            <div className="stat">
              <span className="stat-value">{userStats.total_winnings || 0}</span>
              <span className="stat-label">Total Winnings</span>
            </div>
            <div className="stat">
              <span className="stat-value">{userStats.win_rate ? userStats.win_rate.toFixed(1) : 0}%</span>
              <span className="stat-label">Win Rate</span>
            </div>
          </div>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="game-tabs">
        <button 
          className={activeTab === 'cash' ? 'tab active' : 'tab'} 
          onClick={() => setActiveTab('cash')}
        >
          Cash Games
        </button>
        <button 
          className={activeTab === 'practice' ? 'tab active' : 'tab'} 
          onClick={() => setActiveTab('practice')}
        >
          Practice Games
        </button>
        <button 
          className={activeTab === 'tournaments' ? 'tab active' : 'tab'} 
          onClick={() => setActiveTab('tournaments')}
        >
          Tournaments
        </button>
      </div>

      <div className="games-section">
        <h3>{activeTab === 'cash' ? 'Cash Games' : activeTab === 'practice' ? 'Practice Games' : 'Tournaments'}</h3>
        
        <div className="games-grid">
          {gameVariants[activeTab].map(game => (
            <div key={game.id} className="game-card">
              <div className="game-header">
                <h4>{game.name}</h4>
                {game.entryFee > 0 && (
                  <span className="entry-fee">Entry: ₹{game.entryFee}</span>
                )}
              </div>
              
              <div className="game-details">
                <div className="detail">
                  <span className="label">Players:</span>
                  <span className="value">{game.minPlayers}-{game.maxPlayers}</span>
                </div>
                
                {game.prize > 0 && (
                  <div className="detail">
                    <span className="label">Prize Pool:</span>
                    <span className="value">₹{game.prize}</span>
                  </div>
                )}
              </div>
              
              <button 
                className="play-btn"
                onClick={() => joinGame(game.id, game.entryFee)}
                disabled={game.entryFee > 0 && (!userStats || userStats.wallet_balance < game.entryFee)}
              >
                {game.entryFee > 0 ? 'Play Now' : 'Practice'}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="actions-grid">
          <button className="action-btn">
            <span className="icon">➕</span>
            Add Funds
          </button>
          <button className="action-btn">
            <span className="icon">🏆</span>
            Leaderboard
          </button>
          <button className="action-btn">
            <span className="icon">📊</span>
            My Stats
          </button>
          <button className="action-btn">
            <span className="icon">⚙️</span>
            Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;